import axios, { AxiosError } from 'axios';
import { convertQueryCommandToQueryString, Equal } from '@llws/dynamic-router-utils';
import config from '@/config';
import { adminRequest } from '@/shared/request-configs/admin-request';

/**
 * An authenticator app the user has set up. `identifier` already reads "Authenticator: <name>".
 */
export interface IAuthenticator {
  id: number;
  identifier: string;
  createdDate: string;
}

export interface IAuthenticatorSetup {
  id: number;
  // Image data URL of the QR code to scan
  qrCode: string;
  // Recovery codes, only returned the first time the user sets up an authenticator
  codes?: string[];
}

/**
 * Fetches the current user's enabled authenticator apps <br>
 * API Endpoint - `/perms-api/users_mfa_config` <br> via {@link adminRequest.get}
 * @param userId - the current user's id
 */
export const getAuthenticators = async (userId: number): Promise<IAuthenticator[]> => {
  const query = convertQueryCommandToQueryString({
    // Never select `token`: it holds the authenticator's secret.
    select: [ 'id', 'identifier', 'createdDate' ],
    where: [
      Equal('userId', `${userId}`),
      Equal('type', 'app'),
      Equal('status', 'enabled'),
    ],
    limit: 100,
  });
  const result = await adminRequest.get<IAuthenticator[]>('/perms-api/users_mfa_config', query);
  return result?.data ?? [];
};

/**
 * Turns off one of the current user's authenticator apps <br>
 * API Endpoint - `/perms-api/users_mfa_config/:id` <br> via {@link adminRequest.put}
 * @param id - id of the authenticator to remove
 */
export const removeAuthenticator = (id: number) => adminRequest.put(`/perms-api/users_mfa_config/${id}`, { status: 'disabled' });

/*
 * The two setup calls below use axios directly instead of `oauthRequest`.
 * The oauth service answers a wrong password or code with a 401, which the shared
 * error handler treats as an expired session: it refreshes the token, retries, and signs
 * the user out when the retry fails again. Handling the error here keeps the user signed in
 * and lets the modal show the message inline.
 */

/**
 * Starts adding an authenticator app: checks the password and returns the QR code to scan <br>
 * API Endpoint - `/auth/user/mfa/enable`
 * @param password - the user's current password
 * @param label - device name the user chose, unique per user
 */
export const startAuthenticatorSetup = async ({ password, label }: { password: string, label: string }) => {
  const response = await axios.post<{ data: IAuthenticatorSetup }>(
    `${config?.oauthService}/auth/user/mfa/enable`,
    { passRaw: password, passHash: '', label },
  );
  return response.data.data;
};

/**
 * Finishes adding an authenticator app by checking a code from it <br>
 * API Endpoint - `/auth/user/mfa/confirm`
 * @param id - id returned by {@link startAuthenticatorSetup}
 * @param code - 6-digit code shown in the authenticator app
 */
export const confirmAuthenticatorSetup = async ({ id, code }: { id: number, code: string }) => {
  await axios.post(`${config?.oauthService}/auth/user/mfa/confirm`, { id, mfaToken: code });
};

/**
 * HTTP status of a failed setup call, or 0 when the request never got a response.
 */
export const getSetupErrorStatus = (err: unknown): number => (err as AxiosError)?.response?.status ?? 0;

export default {
  getAuthenticators,
  removeAuthenticator,
  startAuthenticatorSetup,
  confirmAuthenticatorSetup,
};
